/**
 * Syncs Kaching bar labels: subscription plan → 49% OFF, one-time → app default (e.g. 30% OFF).
 * Supports kaching-subscriptions and kaching-bundles-block (incl. shadow DOM).
 */
(function () {
  const SUBSCRIPTION_BADGE = '49% OFF';
  const THIRTY_PERCENT_PATTERN = /30\s*%/i;
  const ZERO_PERCENT_PATTERN = /^0\s*%/i;
  const WIDGET_SELECTORS = ['kaching-subscriptions', 'kaching-bundles-block'];
  const defaultLabelByElement = new WeakMap();
  const defaultDisplayByElement = new WeakMap();
  const boundWidgets = new WeakSet();
  let waitObserver = null;
  let waitTimeout = null;

  function getWidgetRoots() {
    const roots = [];

    WIDGET_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((widget) => {
        roots.push(widget);
        if (widget.shadowRoot) {
          roots.push(widget.shadowRoot);
        }
      });
    });

    return roots;
  }

  function queryAll(selector) {
    const nodes = [];
    const seen = new Set();

    getWidgetRoots().forEach((root) => {
      root.querySelectorAll(selector).forEach((node) => {
        if (!seen.has(node)) {
          seen.add(node);
          nodes.push(node);
        }
      });
    });

    if (!nodes.length) {
      document.querySelectorAll(selector).forEach((node) => {
        if (!seen.has(node)) {
          seen.add(node);
          nodes.push(node);
        }
      });
    }

    return nodes;
  }

  function isSubscriptionBar(bar) {
    if (!bar) {
      return false;
    }

    if (bar.querySelector('.kaching-bundles__subscriptions__card')) {
      return true;
    }

    const text = bar.textContent || '';
    return /flexible\s+plan|subscribe|subscription|delivered\s+every/i.test(text);
  }

  function isSubscriptionPlanSelected() {
    return queryAll('.kaching-bundles__bar--selected').some(isSubscriptionBar);
  }

  function isZeroPercentLabel(label) {
    return ZERO_PERCENT_PATTERN.test(label.textContent.trim());
  }

  function setLabelHidden(label, hidden) {
    if (hidden) {
      if (!defaultDisplayByElement.has(label)) {
        defaultDisplayByElement.set(label, label.style.display);
      }

      label.style.display = 'none';
      label.setAttribute('aria-hidden', 'true');
      return;
    }

    if (defaultDisplayByElement.has(label)) {
      label.style.display = defaultDisplayByElement.get(label);
      defaultDisplayByElement.delete(label);
    } else {
      label.style.removeProperty('display');
    }

    label.removeAttribute('aria-hidden');
  }

  function cacheDefaultLabels() {
    queryAll('.kaching-bundles__bar-label').forEach((label) => {
      if (defaultLabelByElement.has(label)) {
        return;
      }

      const text = label.textContent.trim();
      if (THIRTY_PERCENT_PATTERN.test(text)) {
        defaultLabelByElement.set(label, text);
      }
    });
  }

  function updateLabels() {
    const subscriptionSelected = isSubscriptionPlanSelected();

    cacheDefaultLabels();

    queryAll('.kaching-bundles__bar-label').forEach((label) => {
      if (isZeroPercentLabel(label)) {
        setLabelHidden(label, true);
        return;
      }

      setLabelHidden(label, false);

      const original = defaultLabelByElement.get(label);
      if (!original) {
        return;
      }

      const next = subscriptionSelected ? SUBSCRIPTION_BADGE : original;
      if (label.textContent.trim() !== next) {
        label.textContent = next;
      }
    });
  }

  function onSelectionChange() {
    requestAnimationFrame(updateLabels);
  }

  function bindWidget(widget) {
    if (!widget || boundWidgets.has(widget)) {
      return;
    }

    boundWidgets.add(widget);

    widget.addEventListener('deal-bar-selected', onSelectionChange);
    widget.addEventListener(
      'click',
      (event) => {
        if (event.target.closest('.kaching-bundles__bar')) {
          onSelectionChange();
        }
      },
      true
    );

    const observeRoot = widget.shadowRoot || widget;
    const selectionObserver = new MutationObserver((mutations) => {
      const selectionChanged = mutations.some(
        (mutation) =>
          mutation.type === 'attributes' &&
          mutation.attributeName === 'class' &&
          mutation.target instanceof Element &&
          mutation.target.classList.contains('kaching-bundles__bar')
      );

      if (selectionChanged) {
        onSelectionChange();
      }
    });

    selectionObserver.observe(observeRoot, {
      attributes: true,
      subtree: true,
      attributeFilter: ['class'],
    });

    let structureTimeout = null;
    const structureObserver = new MutationObserver(() => {
      clearTimeout(structureTimeout);
      structureTimeout = setTimeout(onSelectionChange, 100);
    });

    structureObserver.observe(observeRoot, {
      childList: true,
      subtree: true,
    });

    onSelectionChange();
  }

  function stopWaiting() {
    if (waitTimeout) {
      clearTimeout(waitTimeout);
      waitTimeout = null;
    }

    if (waitObserver) {
      waitObserver.disconnect();
      waitObserver = null;
    }
  }

  function tryBindAll() {
    let found = false;

    WIDGET_SELECTORS.forEach((selector) => {
      document.querySelectorAll(selector).forEach((widget) => {
        bindWidget(widget);
        found = true;
      });
    });

    if (found) {
      stopWaiting();
    }

    return found;
  }

  function waitForWidgets() {
    if (tryBindAll()) {
      return;
    }

    waitObserver = new MutationObserver(() => {
      clearTimeout(waitTimeout);
      waitTimeout = setTimeout(tryBindAll, 150);
    });

    waitObserver.observe(document.body, { childList: true, subtree: true });
    setTimeout(stopWaiting, 30000);
  }

  document.addEventListener(
    'click',
    (event) => {
      if (event.target.closest('.kaching-bundles__bar')) {
        onSelectionChange();
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', waitForWidgets, { once: true });
  } else {
    waitForWidgets();
  }
})();
